import React, { useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import '../setupChart'; // register chart components + plugin
import { api as analyticsApi } from '../hooks/useApi';

export default function PerformanceCharts({ 
  chartData, 
  powerData, 
  onChartClick, 
  showPowerChart = true,
  selectedFan = null,
  certificates = [],
  motorsForCategory = [],
  selectedMotorForPrint = null,
  setSelectedMotorForPrint = null,
  toPublicUrl = null,
  showTabs = true, // New prop: controls whether tabs are displayed
  airFlowUnit = 'm3h', // User's selected airflow unit
  pressureUnit = 'pa' // User's selected pressure unit
}) {
  const chartRef = useRef(null);
  const smallRef = useRef(null);
  const [activeTab, setActiveTab] = useState('performance');

  // Helper function to convert image paths to public URLs
  const getImageUrl = (path) => {
    if (!path) return '';
    if (toPublicUrl) return toPublicUrl(path);
    if (typeof path !== 'string') return '';
    const p = path.trim();
    if (/^https?:\/\//i.test(p)) {
      // Replace api.apt.com.co with apt.com.co for images
      return p.replace(/api\.apt\.com\.co/gi, 'apt.com.co');
    }
    const apiBase = process.env.REACT_APP_API_BASE_URL || '';
    const laravelBase = apiBase.replace(/\/api$/i, '') || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8000');
    let url = '';
    if (p.startsWith('public/')) url = `${laravelBase}/storage/${p.slice('public/'.length)}`;
    else if (p.startsWith('storage/')) url = `${laravelBase}/${p}`;
    else if (p.startsWith('/storage/')) url = `${laravelBase}${p}`;
    else url = `${laravelBase}/storage/${p}`;
    // Replace api.apt.com.co with apt.com.co for images
    return url.replace(/api\.apt\.com\.co/gi, 'apt.com.co');
  };

  const handleCertificateDownload = (cert) => {
    if (!cert?.image) return;
    const url = getImageUrl(cert.image);
    if (!url) return;

    window.open(url, '_blank', 'noopener');

    if (cert?.id) {
      analyticsApi.post(`/api/analytics/certificates/${cert.id}`, {
        fan_id: selectedFan?.id,
      }).catch(() => {});
    }
  };

  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper to get airflow unit label
  const getAirflowUnitLabel = (unit) => {
    switch (unit) {
      case 'm3h': return 'm³/h';
      case 'm3s': return 'm³/s';
      case 'ls': return 'l/s';
      case 'cfm': return 'cfm';
      default: return 'm³/h';
    }
  };

  // Helper to get pressure unit label
  const getPressureUnitLabel = (unit) => {
    switch (unit) {
      case 'pa': return 'Pa';
      case 'mmh2o': return 'mm H₂O';
      case 'inchh2o': return 'inch H₂O';
      case 'mbar': return 'mbar';
      case 'kpa': return 'kPa';
      default: return 'Pa';
    }
  };

  // Helper function to calculate nice step size
  const calculateNiceStep = (min, max, maxTicks = 12) => {
    if (!isFinite(min) || !isFinite(max) || min >= max) return null;
    const range = max - min;
    const roughStep = range / maxTicks;
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const normalized = roughStep / magnitude;
    let niceStep;
    // Use smaller steps for more granular ticks
    if (normalized <= 0.5) niceStep = 0.5;
    else if (normalized <= 1) niceStep = 1;
    else if (normalized <= 2) niceStep = 2;
    else if (normalized <= 2.5) niceStep = 2.5;
    else if (normalized <= 5) niceStep = 5;
    else niceStep = 10;
    return niceStep * magnitude;
  };

  // Helper function to round to nice number
  const roundToNiceNumber = (value, step) => {
    if (!step || !isFinite(value)) return value;
    return Math.ceil(value / step) * step;
  };

  // Calculate min/max from all datasets to ensure proper scaling
  const calculateAxisLimits = () => {
    if (!chartData || !chartData.datasets) {
      return { xMin: 0, xMax: undefined, yMin: 0, yMax: undefined };
    }

    let xMin = Infinity;
    let xMax = -Infinity;
    let yMin = Infinity;
    let yMax = -Infinity;

    chartData.datasets.forEach((dataset) => {
      if (dataset.data && Array.isArray(dataset.data) && dataset.data.length > 0) {
        dataset.data.forEach((point) => {
          if (point && typeof point.x === 'number' && !isNaN(point.x) && isFinite(point.x)) {
            xMin = Math.min(xMin, point.x);
            xMax = Math.max(xMax, point.x);
          }
          if (point && typeof point.y === 'number' && !isNaN(point.y) && isFinite(point.y)) {
            yMin = Math.min(yMin, point.y);
            yMax = Math.max(yMax, point.y);
          }
        });
      }
    });

    // If no valid data found, return undefined to let Chart.js auto-scale
    if (!isFinite(xMin) || !isFinite(xMax) || !isFinite(yMin) || !isFinite(yMax)) {
      return { xMin: 0, xMax: undefined, yMin: 0, yMax: undefined };
    }

    // Add padding (10% on each side, minimum 5% of range)
    const xRange = xMax - xMin;
    const yRange = yMax - yMin;
    const xPadding = Math.max(xRange * 0.1, xRange * 0.05);
    const yPadding = Math.max(yRange * 0.1, yRange * 0.05);

    const xMaxPadded = xMax + xPadding;
    const yMaxPadded = yMax + yPadding;

    return {
      xMin: Math.max(0, xMin - xPadding),
      xMax: xMaxPadded,
      yMin: Math.max(0, yMin - yPadding),
      yMax: yMaxPadded,
    };
  };

  const axisLimits = calculateAxisLimits();

  const xStep = axisLimits.xMax ? calculateNiceStep(axisLimits.xMin, axisLimits.xMax, 12) : null;
  const yStep = axisLimits.yMax ? calculateNiceStep(axisLimits.yMin, axisLimits.yMax, 12) : null;

  const perfOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: isMobile ? 1.5 : 2,
    scales: {
      x: {
        type: 'linear',
        title: { display: true, text: `Air Flow (${getAirflowUnitLabel(airFlowUnit)})` },
        min: axisLimits.xMin,
        max: axisLimits.xMax ? roundToNiceNumber(axisLimits.xMax, xStep) : undefined,
        ticks: {
          maxTicksLimit: 15,
          stepSize: xStep || undefined,
          callback: function(value) {
            // Format with reasonable decimal places based on step size
            if (!xStep || xStep >= 1) {
              return Math.round(value);
            } else if (xStep >= 0.1) {
              return value.toFixed(1);
            } else {
              return value.toFixed(2);
            }
          }
        }
      },
      y: {
        title: { display: true, text: `Static Pressure (${getPressureUnitLabel(pressureUnit)})` },
        min: axisLimits.yMin,
        max: axisLimits.yMax ? roundToNiceNumber(axisLimits.yMax, yStep) : undefined,
        ticks: {
          maxTicksLimit: 15,
          stepSize: yStep || undefined,
          callback: function(value) {
            // Format with reasonable decimal places based on step size
            if (!yStep || yStep >= 1) {
              return Math.round(value);
            } else if (yStep >= 0.1) {
              return value.toFixed(1);
            } else {
              return value.toFixed(2);
            }
          }
        }
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
      }
    }
  };

  // Calculate nice steps for power chart
  const calculatePowerAxisLimits = () => {
    if (!powerData || !powerData.datasets) {
      return { xStep: null, yStep: null, xMax: undefined, yMax: undefined };
    }

    let xMin = Infinity;
    let xMax = -Infinity;
    let yMin = Infinity;
    let yMax = -Infinity;

    powerData.datasets.forEach((dataset) => {
      if (dataset.data && Array.isArray(dataset.data) && dataset.data.length > 0) {
        dataset.data.forEach((point) => {
          if (point && typeof point.x === 'number' && !isNaN(point.x) && isFinite(point.x)) {
            xMin = Math.min(xMin, point.x);
            xMax = Math.max(xMax, point.x);
          }
          if (point && typeof point.y === 'number' && !isNaN(point.y) && isFinite(point.y)) {
            yMin = Math.min(yMin, point.y);
            yMax = Math.max(yMax, point.y);
          }
        });
      }
    });

    if (!isFinite(xMin) || !isFinite(xMax) || !isFinite(yMin) || !isFinite(yMax)) {
      return { xStep: null, yStep: null, xMax: undefined, yMax: undefined };
    }

    const xPadding = Math.max((xMax - xMin) * 0.1, (xMax - xMin) * 0.05);
    const yPadding = Math.max((yMax - yMin) * 0.1, (yMax - yMin) * 0.05);

    const xMaxPadded = xMax + xPadding;
    const yMaxPadded = yMax + yPadding;

    const xStep = calculateNiceStep(0, xMaxPadded, 12);
    const yStep = calculateNiceStep(0, yMaxPadded, 12);

    return {
      xStep,
      yStep,
      xMax: isFinite(xMaxPadded) && xStep ? roundToNiceNumber(xMaxPadded, xStep) : undefined,
      yMax: isFinite(yMaxPadded) && yStep ? roundToNiceNumber(yMaxPadded, yStep) : undefined,
    };
  };

  const powerLimits = calculatePowerAxisLimits();

  const powerOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: isMobile ? 1.5 : 2,
    scales: {
      x: {
        type: 'linear',
        title: { display: true, text: `Air Flow (${getAirflowUnitLabel(airFlowUnit)})` },
        min: 0,
        max: powerLimits.xMax,
        ticks: {
          maxTicksLimit: 15,
          stepSize: powerLimits.xStep || undefined,
          callback: function(value) {
            if (!powerLimits.xStep || powerLimits.xStep >= 1) {
              return Math.round(value);
            } else if (powerLimits.xStep >= 0.1) {
              return value.toFixed(1);
            } else {
              return value.toFixed(2);
            }
          }
        }
      },
      y: {
        title: { display: true, text: 'Shaft Power (kW)' },
        min: 0,
        max: powerLimits.yMax,
        ticks: {
          maxTicksLimit: 15,
          stepSize: powerLimits.yStep || undefined,
          callback: function(value) {
            if (!powerLimits.yStep || powerLimits.yStep >= 1) {
              return Math.round(value);
            } else if (powerLimits.yStep >= 0.1) {
              return value.toFixed(1);
            } else {
              return value.toFixed(2);
            }
          }
        }
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
      }
    }
  };

  // If tabs are disabled, show both charts stacked (performance chart on top, power chart below)
  if (!showTabs) {
    return (
      <div className="mb-4">
        <div className="w-100">
          <div className="tab-content" style={{ padding: '1rem', border: '1px solid #dee2e6', borderTop: 'none' }}>
            {/* Performance Chart (Air Flow vs Pressure) */}
            <div className="tab-pane fade show active" id="performance" role="tabpanel" style={{ marginBottom: powerData && showPowerChart ? '2rem' : '0' }}>
              <div className="chart-container">
                <Line
                  ref={chartRef}
                  data={chartData}
                  options={perfOptions}
                  onClick={onChartClick}
                />
              </div>
            </div>
            {/* Shaft Power Chart (below performance chart) */}
            {showPowerChart && powerData && (
              <div className="tab-pane fade show active" id="power-stacked" role="tabpanel" style={{ marginTop: '2rem' }}>
                <h6 className="text-center" style={{ marginBottom: '1rem', fontSize: '14px', fontWeight: 600 }}>Shaft Power vs Air Flow</h6>
                <div className="chart-container">
                  <Line
                    ref={smallRef}
                    data={powerData}
                    options={powerOptions}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <div className="w-100">
        <ul className="nav nav-tabs" role="tablist" style={{ flexWrap: 'wrap', overflowX: 'auto' }}>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === 'performance' ? 'active' : ''}`}
              id="performance-tab"
              type="button"
              role="tab"
              onClick={() => setActiveTab('performance')}
            >
              Performance Chart
            </button>
          </li>

          {showPowerChart && (
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'power' ? 'active' : ''}`}
                id="power-tab"
                type="button"
                role="tab"
                onClick={() => setActiveTab('power')}
              >
                Power - Efficiency
              </button>
            </li>
          )}

          {selectedFan && (
            <>
              {selectedFan.sound_power_level_image && (
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${activeTab === 'acoustic' ? 'active' : ''}`}
                    id="acoustic-tab"
                    type="button"
                    role="tab"
                    onClick={() => setActiveTab('acoustic')}
                  >
                    Acoustic
                  </button>
                </li>
              )}

              {selectedFan.dimension_image && (
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${activeTab === 'dimension' ? 'active' : ''}`}
                    id="dimension-tab"
                    type="button"
                    role="tab"
                    onClick={() => setActiveTab('dimension')}
                  >
                    Dimension
                  </button>
                </li>
              )}

              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'faninfo' ? 'active' : ''}`}
                  id="faninfo-tab"
                  type="button"
                  role="tab"
                  onClick={() => setActiveTab('faninfo')}
                >
                  Fan Info
                </button>
              </li>

              {motorsForCategory && motorsForCategory.length > 0 && (
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${activeTab === 'motor' ? 'active' : ''}`}
                    id="motor-tab"
                    type="button"
                    role="tab"
                    onClick={() => setActiveTab('motor')}
                  >
                    Motor
                  </button>
                </li>
              )}

              {certificates && certificates.length > 0 && (
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${activeTab === 'certificate' ? 'active' : ''}`}
                    id="certificate-tab"
                    type="button"
                    role="tab"
                    onClick={() => setActiveTab('certificate')}
                  >
                    Certificate
                  </button>
                </li>
              )}
            </>
          )}
        </ul>

        <div className="tab-content" style={{ padding: '1rem', border: '1px solid #dee2e6', borderTop: 'none' }}>
          <div
            className={`tab-pane fade ${activeTab === 'performance' ? 'show active' : ''}`}
            id="performance"
            role="tabpanel"
            aria-labelledby="performance-tab"
          >
            <div className="chart-container">
              <Line
                ref={chartRef}
                data={chartData}
                options={perfOptions}
                onClick={onChartClick}
              />
            </div>
          </div>

          {showPowerChart && (
            <div
              className={`tab-pane fade ${activeTab === 'power' ? 'show active' : ''}`}
              id="power"
              role="tabpanel"
              aria-labelledby="power-tab"
            >
              <h6 className="text-center">Power vs Air Flow</h6>
              <div className="chart-container">
                <Line
                  ref={smallRef}
                  data={powerData}
                  options={powerOptions}
                />
              </div>
            </div>
          )}

          {selectedFan && selectedFan.sound_power_level_image && (
            <div
              className={`tab-pane fade ${activeTab === 'acoustic' ? 'show active' : ''}`}
              id="acoustic"
              role="tabpanel"
              aria-labelledby="acoustic-tab"
            >
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <img
                  src={getImageUrl(selectedFan.sound_power_level_image)}
                  alt="Sound Power Level"
                  style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
                />
              </div>
            </div>
          )}

          {selectedFan && selectedFan.dimension_image && (
            <div
              className={`tab-pane fade ${activeTab === 'dimension' ? 'show active' : ''}`}
              id="dimension"
              role="tabpanel"
              aria-labelledby="dimension-tab"
            >
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <img
                  src={getImageUrl(selectedFan.dimension_image)}
                  alt="Dimension"
                  style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
                />
              </div>
            </div>
          )}

          {selectedFan && (
            <div
              className={`tab-pane fade ${activeTab === 'faninfo' ? 'show active' : ''}`}
              id="faninfo"
              role="tabpanel"
              aria-labelledby="faninfo-tab"
            >
              <div style={{ padding: '20px' }}>
                <h6>Fan Information</h6>
                <div className="table-responsive">
                  <table className="table table-sm">
                    <tbody>
                      <tr>
                        <th style={{ width: '40%', background: '#fbfdff' }}>Model Name</th>
                        <td>{selectedFan.model_name ?? selectedFan.model ?? selectedFan.name ?? '-'}</td>
                      </tr>
                      <tr>
                        <th style={{ background: '#fbfdff' }}>Manufacturer</th>
                        <td>{selectedFan.manufacturer ?? '-'}</td>
                      </tr>
                      <tr>
                        <th style={{ background: '#fbfdff' }}>SKU</th>
                        <td>{selectedFan.sku ?? '-'}</td>
                      </tr>
                      <tr>
                        <th style={{ background: '#fbfdff' }}>Diameter</th>
                        <td>{selectedFan.diameter != null ? `${selectedFan.diameter} mm` : '-'}</td>
                      </tr>
                      <tr>
                        <th style={{ background: '#fbfdff' }}>Max Speed (RPM)</th>
                        <td>{selectedFan.max_speed_rpm != null ? `${selectedFan.max_speed_rpm}` : '-'}</td>
                      </tr>
                      {selectedFan.description && (
                        <tr>
                          <th style={{ background: '#fbfdff' }}>Description</th>
                          <td>
                            <div 
                              className="rich-content" 
                              dangerouslySetInnerHTML={{ __html: selectedFan.description }} 
                            />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {selectedFan && motorsForCategory && motorsForCategory.length > 0 && (
            <div
              className={`tab-pane fade ${activeTab === 'motor' ? 'show active' : ''}`}
              id="motor"
              role="tabpanel"
              aria-labelledby="motor-tab"
            >
              <div style={{ padding: '20px' }}>
                <h6>Motor (Auto-Selected)</h6>
                {selectedMotorForPrint && (
                  <div>
                    {selectedMotorForPrint.image && (
                      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                        <img
                          src={getImageUrl(selectedMotorForPrint.image)}
                          alt="Motor"
                          style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain' }}
                        />
                      </div>
                    )}
                    <div className="table-responsive">
                      <table className="table table-sm">
                        <tbody>
                          <tr>
                            <th style={{ width: '40%', background: '#fbfdff' }}>Name</th>
                            <td>{selectedMotorForPrint.name ?? selectedMotorForPrint.model ?? '-'}</td>
                          </tr>
                          <tr>
                            <th style={{ background: '#fbfdff' }}>Manufacturer</th>
                            <td>{selectedMotorForPrint.manufacturer ?? '-'}</td>
                          </tr>
                          <tr>
                            <th style={{ background: '#fbfdff' }}>Nominal Power</th>
                            <td>{selectedMotorForPrint.nominal_motor_power ?? selectedMotorForPrint.power ?? '-'} kW</td>
                          </tr>
                          <tr>
                            <th style={{ background: '#fbfdff' }}>Number of Poles</th>
                            <td>{selectedMotorForPrint.no_of_poles ?? selectedMotorForPrint.noOfPoles ?? '-'}</td>
                          </tr>
                          {selectedMotorForPrint.description && (
                            <tr>
                              <th style={{ background: '#fbfdff' }}>Description</th>
                              <td>{selectedMotorForPrint.description}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {selectedFan && certificates && certificates.length > 0 && (
            <div
              className={`tab-pane fade ${activeTab === 'certificate' ? 'show active' : ''}`}
              id="certificate"
              role="tabpanel"
              aria-labelledby="certificate-tab"
            >
              <div style={{ padding: '20px' }}>
                <h6>Certificates</h6>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', justifyContent: 'center' }}>
                  {certificates.map((cert) => (
                    <div key={cert.id} style={{ textAlign: 'center', border: '1px solid #dee2e6', padding: '15px', borderRadius: '8px', background: '#fff' }}>
                      {cert.image && (
                        <img
                          src={getImageUrl(cert.image)}
                          alt={cert.name}
                          style={{ maxWidth: 150, maxHeight: 150, objectFit: 'contain', marginBottom: '10px' }}
                        />
                      )}
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{cert.name}</div>
                      <button
                        type="button"
                        onClick={() => handleCertificateDownload(cert)}
                        style={{
                          marginTop: '8px',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: '1px solid rgba(37, 99, 235, 0.4)',
                          background: '#f8fafc',
                          color: '#1d4ed8',
                          fontSize: '13px',
                          cursor: 'pointer',
                        }}
                      >
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
